import type { Tag, CreateTagPayload, UpdateTagPayload } from "@/types/tag";

import { useMockStore } from "../store";
import { createId, createPastTimestamp } from "../generators/base";

/**
 * Get all tags
 */
export function getTagsMock(): Tag[] {
  const store = useMockStore.getState();

  return store.tags;
}

/**
 * Create a new tag
 */
export function createTagMock(payload: CreateTagPayload): Tag {
  const store = useMockStore.getState();
  const timestamp = createPastTimestamp(0);

  const newTag: Tag = {
    id: createId(),
    name: payload.name,
    color: payload.color,
    scopes: payload.scopes,
    createdAt: timestamp,
    updatedAt: timestamp,
    attachedAt: timestamp,
  };

  store.addTag(newTag);

  return newTag;
}

/**
 * Update a tag
 */
export function updateTagMock(
  tagId: string,
  payload: UpdateTagPayload,
): Tag | undefined {
  const store = useMockStore.getState();
  const tag = store.tags.find((t) => t.id === tagId);

  if (!tag) return undefined;

  const updates: Partial<Tag> = {
    ...payload,
    updatedAt: new Date().toISOString(),
  };

  store.updateTag(tagId, updates);

  return { ...tag, ...updates };
}

/**
 * Delete a tag
 */
export function deleteTagMock(tagId: string): boolean {
  const store = useMockStore.getState();
  const exists = store.tags.some((t) => t.id === tagId);

  if (exists) {
    store.deleteTag(tagId);

    return true;
  }

  return false;
}

/**
 * Tag action (attach/detach tags to/from contacts)
 */
export function tagActionMock(
  action: "attach" | "detach",
  contactIds: Array<string>,
  tagIds: Array<string>,
): void {
  const store = useMockStore.getState();
  if (tagIds.some((id) => !store.tags.some((tag) => tag.id === id))) {
    throw new Error("Tag not found");
  }
  for (const contact of store.contacts.filter((item) =>
    contactIds.includes(item.id),
  )) {
    const tags =
      action === "attach"
        ? [...new Set([...contact.tags, ...tagIds])]
        : contact.tags.filter((id) => !tagIds.includes(id));
    store.updateContact(contact.id, { tags });
  }
}
